import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';

import Input from './Input';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [cartCount, setCartCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);

  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      const cartItems = JSON.parse(savedCart);
      setCartCount(cartItems?.length);
    }
  }, []);

  useEffect(() => {
    const handleCartUpdate = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const cartItems = JSON.parse(savedCart);
        setCartCount(cartItems?.length);
      } else {
        setCartCount(0);
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate(`/e-book-catalog?search=${encodeURIComponent(searchQuery)}`);
      setIsSearchExpanded(false);
      setIsMobileMenuOpen(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen(false);
  };

  const handleLogout = async () => {
    await signOut();
    setIsAccountMenuOpen(false);
    navigate('/e-book-catalog');
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-100 bg-card shadow-md">
      <div className="max-w-[1536px] mx-auto">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-8">
            <button
              onClick={() => handleNavigation('/e-book-catalog')}
              className="flex items-center gap-3 transition-smooth hover:opacity-80 focus-ring"
              aria-label="StrategyShelf Home"
            >
              <img
                src="/assets/images/Logo_final_no_background-1771860991795-1772040132991.png"
                alt="StrategyShelf Logo"
                className="h-[100px] w-auto object-contain"
              />
            </button>

            <nav className="hidden lg:flex items-center gap-1">
              <button
                onClick={() => handleNavigation('/e-book-catalog')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth hover:bg-muted focus-ring ${
                  isActivePath('/e-book-catalog')
                    ? 'text-primary bg-primary/10' :'text-foreground'
                }`}
              >
                Browse
              </button>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <form
              onSubmit={handleSearchSubmit}
              className={`hidden md:flex items-center transition-smooth ${
                isSearchExpanded ? 'w-80' : 'w-64'
              }`}
            >
              <div className="relative w-full">
                <Input
                  type="search"
                  placeholder="Search eBooks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  onFocus={() => setIsSearchExpanded(true)}
                  onBlur={() => setIsSearchExpanded(false)}
                  className="pr-10"
                />
                <button
                  type="submit"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-smooth focus-ring"
                  aria-label="Search"
                >
                  <Icon name="Search" size={20} />
                </button>
              </div>
            </form>

            <button
              onClick={() => handleNavigation('/shopping-cart')}
              className="relative p-2 rounded-md hover:bg-muted transition-smooth focus-ring hidden md:flex"
              aria-label={`Shopping cart with ${cartCount} items`}
            >
              <Icon name="ShoppingCart" size={24} color="var(--color-foreground)" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-xs font-data font-medium">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </button>

            <div className="relative hidden md:block">
              <button
                onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                className="p-2 rounded-md hover:bg-muted transition-smooth focus-ring"
                aria-label="Account menu"
                aria-expanded={isAccountMenuOpen}
              >
                <Icon name="User" size={24} color="var(--color-foreground)" />
              </button>

              {isAccountMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsAccountMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-md shadow-lg z-50">
                    {user ? (
                      <>
                        <button
                          onClick={() => handleNavigation('/account')}
                          className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2"
                        >
                          <Icon name="User" size={16} />
                          <span>My Account</span>
                        </button>
                        <button
                          onClick={() => handleNavigation('/downloads')}
                          className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2"
                        >
                          <Icon name="Download" size={16} />
                          <span>My Downloads</span>
                        </button>
                        <hr className="border-border" />
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2 text-sm text-error hover:bg-error/10 transition-smooth flex items-center gap-2"
                        >
                          <Icon name="LogOut" size={16} />
                          <span>Sign Out</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleNavigation('/sign-in')}
                          className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2"
                        >
                          <Icon name="LogIn" size={16} />
                          <span>Sign In</span>
                        </button>
                        <button
                          onClick={() => handleNavigation('/sign-up')}
                          className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2"
                        >
                          <Icon name="UserPlus" size={16} />
                          <span>Sign Up</span>
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md hover:bg-muted transition-smooth focus-ring lg:hidden"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-background z-200 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="fixed top-16 left-0 right-0 bg-card shadow-lg z-300 lg:hidden">
              <div className="p-6 space-y-6">
                <form onSubmit={handleSearchSubmit} className="md:hidden">
                  <div className="relative">
                    <Input
                      type="search"
                      placeholder="Search eBooks..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      className="pr-10"
                    />
                    <button
                      type="submit"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-smooth focus-ring"
                      aria-label="Search"
                    >
                      <Icon name="Search" size={20} />
                    </button>
                  </div>
                </form>

                <nav className="space-y-2">
                  <button
                    onClick={() => handleNavigation('/e-book-catalog')}
                    className={`w-full px-4 py-3 rounded-md text-left font-medium transition-smooth flex items-center gap-3 ${
                      isActivePath('/e-book-catalog')
                        ? 'text-primary bg-primary/10' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="BookOpen" size={20} />
                    Browse
                  </button>

                  <button
                    onClick={() => handleNavigation('/shopping-cart')}
                    className={`w-full px-4 py-3 rounded-md text-left font-medium transition-smooth flex items-center justify-between ${
                      isActivePath('/shopping-cart')
                        ? 'text-primary bg-primary/10' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon name="ShoppingCart" size={20} />
                      Cart
                    </span>
                    {cartCount > 0 && (
                      <span className="w-6 h-6 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-xs font-data font-medium">
                        {cartCount > 9 ? '9+' : cartCount}
                      </span>
                    )}
                  </button>
                </nav>

                <div className="pt-4 border-t border-border space-y-2">
                  {user ? (
                    <>
                      <button
                        onClick={() => handleNavigation('/account')}
                        className="w-full text-left px-4 py-3 text-base text-foreground hover:bg-muted transition-smooth flex items-center gap-3"
                      >
                        <Icon name="User" size={20} />
                        <span>My Account</span>
                      </button>
                      <button
                        onClick={() => handleNavigation('/downloads')}
                        className="w-full text-left px-4 py-3 text-base text-foreground hover:bg-muted transition-smooth flex items-center gap-3"
                      >
                        <Icon name="Download" size={20} />
                        <span>My Downloads</span>
                      </button>
                      <hr className="border-border" />
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-3 text-base text-error hover:bg-error/10 transition-smooth flex items-center gap-3"
                      >
                        <Icon name="LogOut" size={20} />
                        <span>Sign Out</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleNavigation('/sign-in')}
                        className="w-full text-left px-4 py-3 text-base text-foreground hover:bg-muted transition-smooth flex items-center gap-3"
                      >
                        <Icon name="LogIn" size={20} />
                        <span>Sign In</span>
                      </button>
                      <button
                        onClick={() => handleNavigation('/sign-up')}
                        className="w-full text-left px-4 py-3 text-base text-foreground hover:bg-muted transition-smooth flex items-center gap-3"
                      >
                        <Icon name="UserPlus" size={20} />
                        <span>Sign Up</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;